import { Icons } from "assets/icons";
import FilterBar from "components/common/FilterBar";
import NoDataFound from "components/common/NoDataFound";
import Paginations from "components/common/Paginations";
import PageHeader from "components/common/PageHeader";
import TableShimmer from "components/common/TableShimmer";
import DataTable from "components/common/table/DataTable";
import { usePermission } from "hooks/usePermission";
import { getActionFlags } from "utils/getActionFlags";
import {
  getMinMaxDate,
  formatUTCDate,
  getLocalizedField,
  CurrencySymbol,
} from "utils/utils";
import { ORDER_BY_OPTIONS } from "utils/constants";
import React, { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  getPosTransactionsList,
  resetFilters,
  setFilter,
} from "store/slices/posTransactionsSlice";

function POSTransactionsList() {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { canAction } = usePermission();
  const { canView } = canAction("report");
  const { view } = Icons || {};

  const {
    loading,
    items,
    totalPages,
    currentPage,
    totalCount,
    filters: { keyword, dates, orderBy },
  } = useSelector((state) => state.PosTransactions);

  const getAllData = (page, action) => {
    const { isReset, isResetKeyword } = getActionFlags(action);
    dispatch(
      getPosTransactionsList({
        pageNo: page || 1,
        perPage: 10,
        orderBy: isReset ? -1 : orderBy,
        keyword: isResetKeyword ? "" : keyword || "",
        dateRange: isReset ? {} : getMinMaxDate(dates),
      }),
    );
  };

  useEffect(() => {
    if (canView) getAllData();
  }, [canView]);

  const handleReset = () => {
    dispatch(resetFilters());
    getAllData(1, { actionType: "RESET" });
  };

  const filters = useMemo(
    () => [
      {
        key: "orderBy",
        placeholder: t("Order By"),
        type: "singleSelect",
        options: ORDER_BY_OPTIONS,
      },
    ],
    [t],
  );

  const columns = useMemo(
    () => [
      {
        header: t("Branch"),
        key: "branchId",
        render: (value) => getLocalizedField(value?.name, language),
      },
      {
        header: t("Staff"),
        key: "staffId",
        render: (value) => value?.name || "-",
      },
      {
        header: t("Customer"),
        key: "userId",
        render: (value) => (<>
        <p className="mb-0">{value?.name || "-"}</p>
        <p className="mb-0">{value?.phone || "-"}</p>
        </>),
      },
      {
        header: t("License Plate"),
        key: "licencePlateNumber",
        render: (value) => (<>
        <p className="mb-0">{value || "-"}</p>
        </>),
      },
      {
        header: t("Amount"),
        key: "amount",
        render: (value) => (
          <>
            <p className="price_symbol">
              <CurrencySymbol width="12" height="14" />
              {value ? `${value}` : "-"}
            </p>
          </>
        ),
      },
      { header: t("Payment Method"), key: "paymentMethod" },
      
      {
        header: t("Created Date"),
        key: "createdAt",
        render: (value) => formatUTCDate(value),
      },
    ],
    [t, language],
  );

  const renderActions = useCallback(
    (item) => (
      <>
        {canView && (
          <img
            src={view}
            width={20}
            height={20}
            alt="view"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/pos-transactions/details?id=${item._id}`);
            }}
          />
        )}
      </>
    ),
    [canView, navigate],
  );

  if (!canView) return null;

  return (
    <div className="page-content">
      <PageHeader title={t("POS Transactions")} />
      <FilterBar
        keyword={keyword}
        setKeyword={(value) => dispatch(setFilter({ key: "keyword", value }))}
        keywordPlaceholder={t("Search by Name ,Phone Number ,License Plate")}
        filters={filters}
        selectedFilters={{ orderBy }}
        onFilterUpdate={(key, value) => dispatch(setFilter({ key, value }))}
        dates={dates}
        setDates={(value) => dispatch(setFilter({ key: "dates", value }))}
        onSearch={getAllData}
        onReset={handleReset}
        searchMaxWidth={400}
      />

      {loading && <TableShimmer rows={10} columns={7} />}
      {!loading && items?.length === 0 && (
        <NoDataFound title={t("No POS transactions found")} />
      )}
      {!loading && items?.length > 0 && (
        <>
          <DataTable
            columns={columns}
            data={items}
            onRowClick={(item) =>
              navigate(`/pos-transactions/details?id=${item._id}`)
            }
            renderActions={renderActions}
            t={t}
          />
          <Paginations
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={totalCount}
            onPageChange={getAllData}
          />
        </>
      )}
    </div>
  );
}

export default POSTransactionsList;
